//* Vue
import type { SetupContext } from "vue";
import { nextTick, computed, reactive, inject, ref } from "vue";
//* 组件引入
import type UiSimplebar from "@various/components/simplebar";
//* 工具函数
import { node, dispose, utility, animations } from "@various/utils";
//* 公共属性
import { UiFormEmitterKey } from "@various/constants";
//* 组件属性
import type { UiTimePickerProps, UiTimePickerEmits } from "./component";
import { analyzes } from "./utils";

export const useComposable = (define: UiTimePickerProps, emits: SetupContext<typeof UiTimePickerEmits>["emit"]) => {
    //* 初始化mitt
    const emitter = inject(UiFormEmitterKey, undefined);

    //* 静态变量
    const vars = {
        timer: <NodeJS.Timeout | undefined>undefined,
        observer: <ResizeObserver | undefined>undefined,
    };

    //* 响应式变量
    const refs = {
        visible: ref<boolean>(false),
    };

    //* 节点列表
    const nodes = {
        sHourser: ref<InstanceType<typeof UiSimplebar>>(),
        eHourser: ref<InstanceType<typeof UiSimplebar>>(),
        sMinuteser: ref<InstanceType<typeof UiSimplebar>>(),
        sSecondser: ref<InstanceType<typeof UiSimplebar>>(),
        eMinuteser: ref<InstanceType<typeof UiSimplebar>>(),
        eSecondser: ref<InstanceType<typeof UiSimplebar>>(),
        container: ref<HTMLElement>(), //* 时间选择器节点
        candidates: ref<HTMLElement>(), //* 候选项容器节点
    };

    //* 计算属性
    const computeds = {
        //* 组件类
        className: computed(() => (refs.visible.value ? "ui-active" : "")),
        sTime: computed(() => analyzes.date(define.modelValue?.start)),
        eTime: computed(() => analyzes.date(define.modelValue?.end)),
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: () => {
            //* 数据初始化
            const number = define.mode == "minutes" ? 2 : 3;
            const result = {
                start: computeds.sTime.value
                    .slice(0, number)
                    .map((value) => value.toString().padStart(2, "0"))
                    .join(":"),
                end: computeds.eTime.value
                    .slice(0, number)
                    .map((value) => value.toString().padStart(2, "0"))
                    .join(":"),
            };

            //* 事件响应
            emits("update:modelValue", result);
            emits("change", result);
            emitter?.emit(define.name, "change");
        },

        //* 显示函数
        show: () => {
            //* 检测窗口是否已开启
            if (refs.visible.value) return methods.hidden();
            else {
                //* 更新控制变量
                refs.visible.value = true;
                //* 下一帧初始化组件
                nextTick(() => {
                    //* 检测是否满足运行条件
                    if (!nodes.container.value || !nodes.candidates.value) return;
                    else {
                        //* 将内容添加到视图容器中
                        node.append(document.body, nodes.candidates.value);
                        //* 隐藏事件
                        window.addEventListener("click", methods.hidden, { capture: true, once: true });
                        //* 注册观察者
                        vars.observer?.disconnect();
                        vars.observer = new ResizeObserver(() => {
                            if (!nodes.container.value || !nodes.candidates.value) return;
                            else {
                                //* 数据初始化
                                const data = { container: nodes.container.value, view: nodes.candidates.value };
                                dispose.boundary.relativeContainerBody(data, {
                                    direction: "bottom",
                                    offset: 8,
                                    align: "center",
                                });

                                //* 位置对齐
                                nodes.sHourser.value?.move(computeds.sTime.value[0] * 30);
                                nodes.sMinuteser.value?.move(computeds.sTime.value[1] * 30);
                                nodes.sSecondser.value?.move(computeds.sTime.value[2] * 30);
                                nodes.eHourser.value?.move(computeds.eTime.value[0] * 30);
                                nodes.eMinuteser.value?.move(computeds.eTime.value[1] * 30);
                                nodes.eSecondser.value?.move(computeds.eTime.value[2] * 30);
                            }
                        });

                        vars.observer.observe(document.documentElement);
                    }
                });
            }
        },

        //* 隐藏函数
        hidden: (ev?: Event): void => {
            //* 检测是否满足运行条件
            if (!nodes.container.value || !nodes.candidates.value) return;
            else {
                //* 初始化数据
                const target = ev?.target as HTMLElement;
                //* 检测是否需要进行关闭候选菜单
                if (target && (node.includes(target, nodes.container.value) || node.includes(target, nodes.candidates.value))) {
                    return window.addEventListener("click", methods.hidden, { capture: true, once: true });
                } else {
                    //* 状态更新
                    refs.visible.value = false;
                    //* 注销观察者
                    vars.observer?.disconnect();
                    //* 表单校验响应
                    emitter?.emit(define.name, "blur");
                }
            }
        },

        //* 时间滚动回调
        scroll: (index: number, node?: InstanceType<typeof UiSimplebar>, finish?: boolean) => {
            //* 数据初始化
            const number = define.mode == "minutes" ? 2 : 3;
            const simulate = { start: computeds.sTime.value, end: computeds.eTime.value };
            const container = node?.$el.querySelector(".ui-simplebar-container");
            if (finish) simulate.end[index] = Math.floor((container?.scrollTop || 0) / 30);
            else {
                simulate.start[index] = Math.floor((container?.scrollTop || 0) / 30);
            }

            //* 数据格式化
            const result = {
                start: simulate.start
                    .slice(0, number)
                    .map((value) => value.toString().padStart(2, "0"))
                    .join(":"),
                end: simulate.end
                    .slice(0, number)
                    .map((value) => value.toString().padStart(2, "0"))
                    .join(":"),
            };

            //* 事件响应
            emits("update:modelValue", result);
            //* 停止滚动0.5s时调整位置
            vars.timer && clearTimeout(vars.timer);
            vars.timer = setTimeout(() => {
                //* 事件响应
                emits("change", result);
                emitter?.emit(define.name, "change");
                nextTick(() => {
                    //* 位置对齐
                    nodes.sHourser.value?.move(computeds.sTime.value[0] * 30);
                    nodes.sMinuteser.value?.move(computeds.sTime.value[1] * 30);
                    nodes.sSecondser.value?.move(computeds.sTime.value[2] * 30);
                    nodes.eHourser.value?.move(computeds.eTime.value[0] * 30);
                    nodes.eMinuteser.value?.move(computeds.eTime.value[1] * 30);
                    nodes.eSecondser.value?.move(computeds.eTime.value[2] * 30);
                });
            }, 500);
        },
    };

    //* 属性列表
    const binds = reactive({
        end: computed(() => {
            return {
                placeholder: define.placeholder.end,
                value: define.modelValue?.end,
            };
        }),

        start: computed(() => {
            return {
                placeholder: define.placeholder.start,
                value: define.modelValue?.start,
            };
        }),

        //* 容器
        container: computed(() => {
            return {
                class: computeds.className.value,
                style: {
                    width: utility.isNumber(define.width) ? define.width + "px" : define.width,
                },
            };
        }),

        //* 候选项容器
        candidates: computed(() => {
            return {
                class: define.classExtraName || "",
                style: {
                    zIndex: define.zIndex,
                },
            };
        }),
    });

    //* 响应事件
    const ons = {
        //* 候选项
        candidates: animations.selector(true, {
            beforeEnter: () => emits("before-enter"),
            beforeLeave: () => emits("before-leave"),
            afterEnter: () => emits("after-enter"),
            afterLeave: () => emits("after-leave"),
        }),
    };

    return { ons, vars, refs, nodes, binds, methods, computeds };
};
